import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useAuth, useData } from '../../App';
import { Button, Modal, Select } from '../UI';
import { api } from '../../services/api';
import { UserRole } from '../../types';
import { formatDay } from '../../utils/format';
import { errorText, Money, useNames } from '../billing/shared';
import { confirmDialog } from '../Dialogs';

// Черновики платёжек в Т-Банке: зарплата по проведённым выплатам и ЕНП на НДФЛ и взносы. Подписываете в Т-Бизнесе.
type Prep = Awaited<ReturnType<typeof api.draftsPrepare>>;
type Result = Awaited<ReturnType<typeof api.createDrafts>>;
type Group = 'NDFL' | 'CONTRIBUTIONS' | 'INJURIES';
const GROUPS: Group[] = ['NDFL', 'CONTRIBUTIONS', 'INJURIES'];
const TITLES: Record<Group, string> = { NDFL: 'ЕНП — НДФЛ', CONTRIBUTIONS: 'ЕНП — страховые взносы', INJURIES: 'Травматизм — взносы в СФР' };
const parse = (s: string) => Number(String(s).replace(/\s/g, '').replace(',', '.')) || 0;

// ИП, подключённые к Т-Банку по API (только администратор) — для галочек «черновик в Т-Банке» в «Зарплате» и «Оплате»
export const TBANK_BIK = '044525974';
let tbankOrgs: Promise<string[]> | null = null;
export const useTbankOrgs = (): string[] => {
    const { user } = useAuth();
    const [ids, setIds] = useState<string[]>([]);
    useEffect(() => {
        if (user?.role !== UserRole.ADMIN) return;
        tbankOrgs = tbankOrgs || api.bankConnections().then(cs => cs.filter(c => c.tokenHint && c.bank === 'tbank').map(c => c.organizationId)).catch(() => { tbankOrgs = null; return []; });
        let alive = true;
        tbankOrgs.then(x => { if (alive) setIds(x); });
        return () => { alive = false; };
    }, [user]);
    return ids;
};

export const PaymentDrafts: React.FC<{ orgIds: string[]; initialOrgId?: string; initialDate?: string; onClose: () => void }> = ({ orgIds, initialOrgId, initialDate, onClose }) => {
    const { state, showNotification } = useData();
    const names = useNames();
    // По умолчанию — ИП и дата последней выплаты зарплаты
    const lastPay = useMemo(() => state.transactions.filter(t => t.kind === 'SALARY' && t.type === 'EXPENSE' && orgIds.includes(t.organizationId))
        .sort((a, b) => b.date.localeCompare(a.date))[0], [state.transactions, orgIds]);
    const [orgId, setOrgId] = useState(initialOrgId || lastPay?.organizationId || orgIds[0] || '');
    const [date, setDate] = useState(initialDate || lastPay?.date || new Date().toISOString().slice(0, 10));
    const [prep, setPrep] = useState<Prep | null>(null);
    const [wallet, setWallet] = useState('');
    const [sal, setSal] = useState<Record<string, { on: boolean; amount: string; purpose: string }>>({});
    const [taxOn, setTaxOn] = useState<Record<string, boolean>>({});
    // Отдельные платёжки: ЕНП на НДФЛ, ЕНП на страховые взносы, травматизм в СФР
    const [pay, setPay] = useState<Record<Group, { on: boolean; amount: string; touched: boolean }>>(
        { NDFL: { on: false, amount: '', touched: false }, CONTRIBUTIONS: { on: false, amount: '', touched: false }, INJURIES: { on: false, amount: '', touched: false } });
    const [oktmo, setOktmo] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<Result | null>(null);

    useEffect(() => {
        if (!orgId || !date) return;
        setPrep(null); setResult(null);
        api.draftsPrepare(orgId, date).then(p => {
            setPrep(p); setWallet(p.wallets[0]?.id || '');
            setSal(Object.fromEntries(p.salaries.map(s => [s.transactionId, { on: !s.drafted && !s.missing.length, amount: String(s.amount), purpose: s.purpose }])));
            const t = Object.fromEntries(p.taxes.map(x => [x.obligationId, x.suggested]));
            setTaxOn(t);
            const sum = (g: Group) => p.taxes.filter(x => t[x.obligationId] && x.type === g).reduce((s, x) => s + x.amount, 0);
            setPay(Object.fromEntries(GROUPS.map(g => [g, { on: sum(g) > 0, amount: sum(g) ? sum(g).toFixed(2) : '', touched: false }])) as typeof pay);
            setOktmo(p.oktmo || '');
        }).catch(e => { showNotification(errorText(e), 'error'); });
    }, [orgId, date, showNotification]);

    // Сумма каждой платёжки = отмеченные налоги этого вида, пока её не поправили вручную
    const sumOf = (g: Group) => (prep?.taxes || []).filter(x => taxOn[x.obligationId] && x.type === g).reduce((s, x) => s + x.amount, 0);
    const sums = GROUPS.map(sumOf).join('|');
    useEffect(() => {
        setPay(p => Object.fromEntries(GROUPS.map(g => [g, p[g].touched ? p[g] : { ...p[g], amount: sumOf(g) ? sumOf(g).toFixed(2) : '' }])) as typeof p);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sums]);
    const isOn = (g: Group) => pay[g].on && parse(pay[g].amount) > 0;
    const item = (g: Group) => isOn(g) ? { amount: parse(pay[g].amount),
        obligationIds: (prep?.taxes || []).filter(x => taxOn[x.obligationId] && x.type === g).map(x => x.obligationId) } : null;

    const chosen = (prep?.salaries || []).filter(s => sal[s.transactionId]?.on);
    const count = chosen.filter(s => !s.registry).length + (chosen.some(s => s.registry) ? 1 : 0) + GROUPS.filter(isOn).length;
    const submit = async () => {
        if (!(await confirmDialog(`Создать ${count} черновик(а) в Т-Банке? Деньги не уйдут, пока вы не подпишете их в Т-Бизнесе.`))) return;
        setBusy(true);
        try {
            const r = await api.createDrafts({ organizationId: orgId, bankAccountId: wallet,
                salaries: chosen.map(s => ({ transactionId: s.transactionId, amount: parse(sal[s.transactionId].amount), purpose: sal[s.transactionId].purpose })),
                ndfl: item('NDFL'), contributions: item('CONTRIBUTIONS'), injuries: item('INJURIES'),
                oktmo: isOn('INJURIES') ? oktmo.trim() || null : null });
            setResult(r);
            showNotification(`Создано черновиков: ${r.created} — подпишите их в Т-Бизнесе`, r.created ? 'success' : 'error');
        } catch (e) { showNotification(errorText(e), 'error'); }
        finally { setBusy(false); }
    };

    return (
        <Modal isOpen onClose={onClose} title="Платёжки в Т-Банк (черновики)">
            <div className="space-y-3 text-sm max-h-[75vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-3 gap-2">
                    <Select label="ИП" value={orgId} onChange={e => setOrgId(e.target.value)} options={orgIds.map(id => ({ value: id, label: names.orgShort(id) }))} />
                    <label className="text-xs text-slate-600">Дата выплаты<input type="date" value={date} onChange={e => setDate(e.target.value)}
                        className="block w-full border border-slate-300 rounded-md px-2 h-9 text-sm" /></label>
                    <Select label="Списать со счёта" value={wallet} onChange={e => setWallet(e.target.value)} options={(prep?.wallets || []).map(w => ({ value: w.id, label: w.label }))} />
                </div>
                {!prep && <div className="text-slate-500"><Loader2 size={14} className="inline animate-spin mr-1" /> Загрузка…</div>}
                {prep && (<>
                    <div>
                        <div className="font-medium text-slate-800 mb-1">Зарплата — выплаты за {formatDay(date)}</div>
                        {prep.salaries.length === 0 && <div className="text-xs text-slate-500">В этот день выплат зарплаты нет — сначала проведите выплату («Зарплата»).</div>}
                        {prep.salaries.map(s => {
                            const v = sal[s.transactionId];
                            if (!v) return null;
                            return (
                                <div key={s.transactionId} className={`border rounded-md p-2 mb-1.5 ${s.missing.length ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
                                    <div className="flex items-center gap-2">
                                        <input type="checkbox" checked={v.on} disabled={!!s.missing.length}
                                            onChange={e => setSal(x => ({ ...x, [s.transactionId]: { ...v, on: e.target.checked } }))} />
                                        <span className="font-medium flex-1">{s.name}</span>
                                        <input value={v.amount} onChange={e => setSal(x => ({ ...x, [s.transactionId]: { ...v, amount: e.target.value } }))}
                                            className="w-28 border border-slate-300 rounded px-2 py-0.5 text-right" />
                                        <span className="text-xs text-slate-500">₽</span>
                                    </div>
                                    <div className="text-[11px] text-slate-500 ml-5">
                                        {s.missing.length ? <span className="text-amber-700">В карточке нет: {s.missing.join(', ')} — заполните в «Справочниках»</span>
                                            : <>{s.bankName || 'счёт'} ···{(s.account || '').slice(-4)}, БИК {s.bik} · {s.registry ? 'реестр зарплатного проекта' : 'платёжное поручение'}</>}
                                        {s.drafted && <span className="ml-2 text-emerald-700">черновик №{s.drafted.number} уже создан</span>}
                                    </div>
                                    <input value={v.purpose} maxLength={210} onChange={e => setSal(x => ({ ...x, [s.transactionId]: { ...v, purpose: e.target.value } }))}
                                        className="mt-1 ml-5 w-[calc(100%-1.25rem)] border border-slate-200 rounded px-2 py-0.5 text-xs" />
                                </div>);
                        })}
                    </div>
                    {GROUPS.map(g => (
                        <div key={g}>
                            <div className="font-medium text-slate-800 mb-1">{TITLES[g]}</div>
                            {prep.taxes.filter(x => x.type === g).length === 0 && <div className="text-xs text-slate-500">Неоплаченного в mb нет — сумму можно ввести вручную.</div>}
                            {prep.taxes.filter(x => x.type === g).map(x => (
                                <label key={x.obligationId} className="flex items-center gap-2 text-xs py-0.5">
                                    <input type="checkbox" checked={!!taxOn[x.obligationId]} onChange={e => setTaxOn(t => ({ ...t, [x.obligationId]: e.target.checked }))} />
                                    <span className="flex-1">{x.description} <span className="text-slate-400">· срок {formatDay(x.dueDate)}</span></span>
                                    <Money value={x.amount} />
                                </label>))}
                            <div className="flex items-center gap-2 mt-1.5 border border-slate-200 rounded-md p-2">
                                <input type="checkbox" checked={pay[g].on} onChange={e => setPay(p => ({ ...p, [g]: { ...p[g], on: e.target.checked } }))} />
                                <span className="flex-1">{g === 'INJURIES' ? 'Платёжка в СФР (Москва)' : `Платёжка ЕНП — ${g === 'NDFL' ? 'НДФЛ' : 'страховые взносы'}`}</span>
                                {g === 'INJURIES' && <input value={oktmo} onChange={e => setOktmo(e.target.value.replace(/\D/g, '').slice(0, 11))} placeholder="ОКТМО ИП"
                                    className={`w-28 border rounded px-2 py-0.5 ${pay.INJURIES.on && !/^\d{8}(\d{3})?$/.test(oktmo) ? 'border-red-400' : 'border-slate-300'}`}
                                    title="ОКТМО по месту регистрации ИП — запомнится" />}
                                <input value={pay[g].amount} onChange={e => setPay(p => ({ ...p, [g]: { ...p[g], amount: e.target.value, touched: true } }))}
                                    className="w-28 border border-slate-300 rounded px-2 py-0.5 text-right" placeholder="0" />
                                <span className="text-xs text-slate-500">₽</span>
                            </div>
                        </div>))}
                    <p className="text-[11px] text-slate-500">ЕНП: {prep.enp.recipientName}, ИНН {prep.enp.inn}, КПП {prep.enp.kpp}, счёт {prep.enp.bankAcnt}, БИК {prep.enp.bankBik},
                        КБК {prep.enp.kbk}, статус 01 — отдельными платёжками на НДФЛ и на взносы; если на ЕНС есть переплата — уменьшите сумму.
                        Травматизм: {prep.sfr.recipientName}, ИНН {prep.sfr.inn}, КПП {prep.sfr.kpp}, счёт {prep.sfr.bankAcnt}, БИК {prep.sfr.bankBik}, КБК {prep.sfr.kbk},
                        статус 08, ОКТМО — по месту регистрации ИП (запомнится).</p>
                    {result && (
                        <div className="border-t pt-2 space-y-1">{result.results.map((r, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                                {r.status === 'CREATED' ? <CheckCircle2 size={14} className="text-emerald-600" /> : <XCircle size={14} className="text-red-600" />}
                                <span className="flex-1">{r.number ? `№${r.number} · ` : ''}{r.recipient} · <Money value={r.amount} /></span>
                                {r.error && <span className="text-red-600">{r.error}</span>}
                            </div>))}
                            {result.created > 0 && <p className="text-xs text-emerald-700">Откройте Т-Бизнес: реестр — в «Зарплатном проекте», платёжки — в «Платежах»; они ждут вашей подписи.</p>}
                        </div>)}
                    <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button variant="secondary" size="sm" onClick={onClose}>Закрыть</Button>
                        <Button size="sm" onClick={submit} disabled={busy || !count || !wallet}>
                            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}Создать черновики ({count})</Button>
                    </div>
                </>)}
            </div>
        </Modal>
    );
};
